/**
 * 河北CA解密流程 composable
 *
 * 参考 hebei-ca-app/src/views/bidder/decrypt-key/index.vue
 * 核心流程：使用 HebcaClient 的 p7UnEnvelop 方法对 encryptedKey 进行解密
 */
import { ref } from 'vue'

let hebcaClient = null
const caUsers = ref([])
const selectedCertId = ref('')
const caLoading = ref(false)
const caError = ref('')

function loadHebcaScript() {
  return new Promise((resolve, reject) => {
    if (window.HebcaClient?.HebcaP11Object) {
      resolve()
      return
    }

    if (window.electronAPI?.readHebcaScript) {
      window.electronAPI.readHebcaScript()
        .then((source) => {
          try {
            const scriptEl = document.createElement('script')
            scriptEl.textContent = source
            document.head.appendChild(scriptEl)
            document.head.removeChild(scriptEl)
            if (window.HebcaClient?.HebcaP11Object) {
              resolve()
            } else {
              reject(new Error('HebcaClient 全局对象未注册，请检查 JS 文件是否完整'))
            }
          } catch (err) {
            reject(err)
          }
        })
        .catch((err) => reject(new Error('通过主进程读取 Hebca 脚本失败: ' + (err?.message || err))))
      return
    }

    const script = document.createElement('script')
    script.src = '/hbca/hebcaP11Object2.js'
    script.onload = () => {
      if (window.HebcaClient?.HebcaP11Object) {
        resolve()
      } else {
        reject(new Error('HebcaClient 全局对象未注册，可能脚本内容被编码破坏'))
      }
    }
    script.onerror = () => reject(new Error('加载河北CA控件失败，请确认已安装河北CA证书助手'))
    document.head.appendChild(script)
  })
}

async function initHebcaClient() {
  if (hebcaClient) return hebcaClient
  await loadHebcaScript()
  return new Promise((resolve, reject) => {
    try {
      hebcaClient = new window.HebcaClient.HebcaP11Object({
        debug: false,
        errorStyle: 'hebca/text',
        success: () => {
          console.log('[河北CA] 控件初始化成功')
          resolve(hebcaClient)
        },
        fail: (e) => {
          console.error('[河北CA] 控件初始化失败:', e)
          hebcaClient = null
          reject(e)
        }
      })
    } catch (e) {
      reject(e)
    }
  })
}

export function useHebcaDecrypt() {
  /**
   * 初始化 CA 控件并获取证书列表
   */
  async function initCA() {
    caLoading.value = true
    caError.value = ''
    try {
      const client = await initHebcaClient()
      const certCount = await new Promise((resolve, reject) => {
        client.getCertCount({ success: (c) => resolve(c), fail: (e) => reject(e) })
      })
      console.log('[河北CA] 证书数量:', certCount)
      if (certCount === 0) throw new Error('未检测到有效的数字证书，请插入USB Key后重试')

      const deviceList = await new Promise((resolve, reject) => {
        client.HBListDeviceAndCert({ success: (l) => resolve(l), fail: (e) => reject(e) })
      })

      const users = []
      if (deviceList?.deviceList) {
        deviceList.deviceList.forEach((device, idx) => {
          if (device.signCertInfo) {
            const cn = device.signCertInfo.certCN || device.signCertInfo.certSN || ''
            users.push({
              name: cn,
              certid: device.deviceSN || `device_${idx}`,
              deviceSN: device.deviceSN,
              certB64: device.signCertInfo.certB64,
              encryptCertB64: device.encryptCertInfo?.certB64 || device.signCertInfo.certB64,
              isPlaceholder: false
            })
          }
        })
      }

      caUsers.value = users
      if (users.length === 1) selectedCertId.value = users[0].certid
      return users
    } catch (err) {
      caError.value = err.message || 'CA初始化失败'
      throw err
    } finally {
      caLoading.value = false
    }
  }

  /**
   * CspLogin - CA登录（解锁UKey）
   * @param {string} password - PIN码，默认 123456
   */
  async function loginCA(password = '123456') {
    if (!hebcaClient) await initHebcaClient()
    return new Promise((resolve, reject) => {
      hebcaClient.cspLogin({
        pin: password,
        success: () => {
          console.log('[河北CA] CspLogin成功')
          resolve(true)
        },
        fail: (e) => reject(e)
      })
    })
  }

  /**
   * CspLogout - CA登出
   */
  async function logoutCA() {
    if (!hebcaClient) return
    return new Promise((resolve, reject) => {
      hebcaClient.cspLogout({
        success: () => {
          console.log('[河北CA] CspLogout成功')
          resolve(true)
        },
        fail: (e) => reject(e)
      })
    })
  }

  /**
   * 检查CA登录状态
   * @param {string} certId - 证书ID
   */
  async function isLogin(certId) {
    if (!hebcaClient) return false
    return new Promise((resolve, reject) => {
      hebcaClient.isLogin({
        certId: certId,
        success: (result) => resolve(result),
        fail: (e) => reject(e)
      })
    })
  }

  /**
   * 解密 encryptedKey（核心方法）
   * 参考 decrypt-key/index.vue handleDecrypt
   * @param {string} pkcs7Data - PKCS7 格式的加密数据（encryptedKey）
   * @returns {Promise<string>} 解密后的对称密钥（plainKey）
   */
  async function p7UnEnvelop(pkcs7Data) {
    if (!hebcaClient) {
      throw new Error('CA 控件实例未初始化')
    }
    return new Promise((resolve, reject) => {
      hebcaClient.p7UnEnvelop({
        pkcs7: pkcs7Data,
        success: (data) => resolve(data),
        fail: (e) => reject(new Error(e?.message || e?.toString() || String(e)))
      })
    })
  }

  /**
   * 获取 HebcaClient 实例
   */
  function getHebcaClient() {
    return hebcaClient
  }

  /**
   * 重置 CA 状态
   */
  function resetCA() {
    hebcaClient = null
    caUsers.value = []
    selectedCertId.value = ''
    caLoading.value = false
    caError.value = ''
  }

  return {
    caUsers,
    selectedCertId,
    caLoading,
    caError,
    initCA,
    loginCA,
    logoutCA,
    isLogin,
    p7UnEnvelop,
    getHebcaClient,
    resetCA
  }
}
